import {
  FlexBoxImageScaleView,
  FlexBoxImageView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { UserProfileDetails } from 'src/replicant/friends/friends.asyncGetters';
import { FriendVisitDetails } from 'src/replicant/visit/visit.asyncGetters';
import { HudMeter } from 'src/game/components/HudMeter';
import uiConfig from 'src/lib/ui/config';
import { getCreativeSuffix } from 'src/lib/utils';
import * as Pet from 'src/replicant/pet';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import { user } from 'src/StateObserver';

const assets = {
  background: 'assets/ui/shared/frames/backdrop_panel.png',
};

export class FriendProfilePetInfoPanel extends FlexBoxImageScaleView {
  private avatar: FlexBoxImageScaleView;
  private nameLabel: LangBitmapFontTextView;
  private speciesLabel: LangBitmapFontTextView;
  private ageLabel: LangBitmapFontTextView;
  private levelLabel: LangBitmapFontTextView;
  private hungerMeter: HudMeter;
  private happinessMeter: HudMeter;
  private data: UserProfileDetails | FriendVisitDetails | undefined;

  public static get assets() {
    return Object.values(assets);
  }

  constructor(superview: View) {
    super({
      superview,
      flex: {
        width: 526,
        height: 180,
        alignItems: 'center',
        paddingHorizontal: 10,
      },
    });

    // Create avatar background
    const background = new FlexBoxImageView({
      superview: this,
      image: assets.background,
      flex: {
        width: 128,
        height: 133,
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    // Create avatar
    this.avatar = new FlexBoxImageScaleView({
      superview: background,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      flex: {
        width: 118,
        height: 118,
      },
    });

    // Create info column
    const infoColumn = new FlexBoxImageView({
      superview: this,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginLeft: 15,
        marginRight: 10,
        height: 120,
        flexGrow: 1,
      },
    });

    const baseLabelOpts: LangBitmapOpts = {
      superview: infoColumn,
      font: 'Body',
      color: '#BF8147',
      verticalAlign: 'center',
      size: 18,
      height: 22,
    };

    // Create name label
    this.nameLabel = new LangBitmapFontTextView({
      ...baseLabelOpts,
      size: 22,
      height: 30,
    });
    getFlexNode(this.nameLabel).flexGrow = 1;

    // Create species label
    this.speciesLabel = new LangBitmapFontTextView(baseLabelOpts);
    getFlexNode(this.speciesLabel).flexGrow = 1;

    // Create age label
    this.ageLabel = new LangBitmapFontTextView(baseLabelOpts);
    getFlexNode(this.ageLabel).flexGrow = 1;

    // Create level label
    this.levelLabel = new LangBitmapFontTextView(baseLabelOpts);
    getFlexNode(this.levelLabel).flexGrow = 1;

    // Create hunger meter
    this.hungerMeter = this.addSubview(
      new HudMeter({
        ...uiConfig.hud.stomachMeter,
        label: uiConfig.hud.meterLabel,
        text: uiConfig.hud.meterText,
      }),
    );
    this.hungerMeter.updateOpts({ scale: 0.95 });

    // Create happiness meter
    this.happinessMeter = this.addSubview(
      new HudMeter({
        ...uiConfig.hud.loveMeter,
        label: uiConfig.hud.meterLabel,
        text: uiConfig.hud.meterText,
      }),
    );
    this.happinessMeter.updateOpts({ scale: 0.95 });
  }

  public setData(data: UserProfileDetails | FriendVisitDetails) {
    this.data = data;

    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user())];
    const species = data.currentEvolution.species;
    const isDead = data.state === 'dead';
    const { max } = Pet.ruleset.attributeBounds;

    this.avatar.image.setImage(
      `assets/species/${species}/${isDead ? 'dead' : 'icon'}.png`,
    );
    this.nameLabel.localeText = () => data.attributes.name;
    this.speciesLabel.localeText = () =>
      evolutions[species]?.[`name_${getCreativeSuffix()}`];
    this.ageLabel.localeText = () => i18n('friendsVisit.confirm.age', data);
    this.levelLabel.localeText = () => `Lv ${data.level}`;

    this.hungerMeter.setMeterValue(data.attributes.stomach / max);
    this.happinessMeter.setMeterValue(data.attributes.love / max);
  }
}
